import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, Environment, SoftwareAccess } from '@netfoundry-ui/shared/model';
import { HateoasResourceOperation } from '@lagoshny/ngx-hateoas-client';
import { HTTP_CLIENT, LoggerService } from '@netfoundry-ui/shared/services';

@Injectable({ providedIn: 'root' })
export class SoftwareAccessService extends HateoasResourceOperation<SoftwareAccess> {
    apiUrl: string;

    constructor(
        private logger: LoggerService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        super(SoftwareAccess);
        this.apiUrl = this.environment.customerURL;
    }

    // Looks up specific software-access resources by id, in one request (the endpoint's `id` filter takes
    // a set). Callers pass only the ids they need: a role points at a single software-access, and the full
    // list can be large for an NF-internal super-user while adding nothing to the display.
    // The entity carries no name of its own, so this confirms the resource exists and is visible; the
    // label still resolves as name || id.
    public getSoftwareAccessByIds(ids: string[]): Promise<SoftwareAccess[]> {
        const wantedIds = (ids || []).filter((id) => !!id);
        if (!wantedIds.length) {
            return Promise.resolve([] as SoftwareAccess[]);
        }
        const headers = new HttpHeaders().set('Accept', 'application/hal+json');
        const params = new HttpParams()
            .set('id', wantedIds.join(','))
            .set('page', '0')
            .set('size', `${wantedIds.length}`);
        return this.http
            .get(`${this.apiUrl}software-accesses`, { headers, params })
            .toPromise()
            .then((result: any) => this.extractSoftwareAccess(result))
            .catch((error: HttpErrorResponse) => {
                this.logger.error(`Software-access request failed with code ${error?.status}`, error?.error);
                return [] as SoftwareAccess[];
            });
    }

    // the software-access list is a HAL collection with the items under _embedded.softwareAccessList
    private extractSoftwareAccess(result: any): SoftwareAccess[] {
        if (!result) {
            return [];
        }
        if (Array.isArray(result)) {
            return result as SoftwareAccess[];
        }
        const embedded = result._embedded;
        if (embedded) {
            if (Array.isArray(embedded.softwareAccessList)) {
                return embedded.softwareAccessList as SoftwareAccess[];
            }
            const collection = Object.values(embedded).find((value) => Array.isArray(value));
            if (collection) {
                return collection as SoftwareAccess[];
            }
        }
        return [];
    }
}
